import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { GridOptions } from "ag-grid-community";

import { ISession } from "../types/ISession";
import { IApiResponse, IApiResponseWithPagination } from "../types/IApiResponse";
import { IFaq } from "../types/IFaq";

import config from "../../app/configuration";
import { RootState } from "../store";
import { setError } from "./appSlice";
import { ICollection, ITheme } from "../types/ICollection";
import { IPagination } from "../types/IPagination";
import { IFilter, IMap, IUpdateMap, ICreateMap } from "../types/IMap";
import { IWorkspace, IWorkspaceUpdate } from "../types/IWorkspace";
import { IUser, IUserPreferences } from "../types/IUser";
import { IMapAction } from "../types/IMapAction";
import { IMapActionType } from "../types/IMapActionType";
import { ActionInvokeRequest, ActionInvokeResponse, ActionPreflightRequest, ActionPreflightResponse } from "../pages/TruTerritory/MapSelectionActions";

// this base query automatically adds the entity id as a query param to each request
//  except if passed `skipEntityId: true` in the params of the query
// it also unwraps the `data`, `meta`, and `status` properties of the response
const baseQueryWithEntityId: BaseQueryFn<string | (FetchArgs & { skipUnwrap?: boolean; skipEntityId?: boolean }), unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // pull the entityID from the app's root state, or set to undefined if skip entity id is set
  const entityID = typeof args !== "string" && args.skipEntityId ? undefined : (api.getState() as RootState).app.entityID;

  // if the entity id is missing, throw a 400 error instead of sending a bad request
  if (!entityID && !(typeof args !== "string" && args.skipEntityId)) {
    return {
      error: {
        status: 400,
        statusText: "No entity selected",
        data: { message: "No entity selected", exception: "NoEntitySelected" },
      },
    };
  }

  // construct the new args with the additional entityID param
  const adjustedArgs = typeof args === "string" ? { url: args, params: { entityID } } : { ...args, params: { entityID, ...(args.params ?? {}) } };

  // make sure credentials include is true
  const result = await fetchBaseQuery({ baseUrl: config.API_ROOT })({ ...adjustedArgs, credentials: "include" }, api, extraOptions);

  if (typeof args !== "string" && args.skipUnwrap) return result;

  if (result.error) {
    api.dispatch(setError((result.error.data as IApiResponse<void>).status));
    return result;
  } else {
    // unwrap .data
    return { ...result, data: (result.data as IApiResponse<unknown>).data };
  }
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithEntityId,
  tagTypes: ["Session", "Faqs", "CollectionFeatures", "Collections", "CollectionAggregates", "Workspaces", "Maps", "MapActions", "MapActionTypes"],
  endpoints: (builder) => ({
    getSession: builder.query<ISession, void>({
      query: () => "/session",
      providesTags: [{ type: "Session", id: "LIST" }],
    }),

    /**
     * Set a user's preferences (formerly "meta")
     */
    updateUserPreferences: builder.mutation<void, { ID: number; preferences: IUserPreferences }>({
      query: ({ ID, preferences }) => ({ method: "PUT", url: `users/${ID}/preferences`, body: preferences }),
      invalidatesTags: [{ type: "Session", id: "LIST" }],
    }),

    getFaqs: builder.query<IFaq[], void>({
      query: () => ({ url: "/faq", skipEntityId: true }),
      providesTags: [{ type: "Faqs", id: "LIST" }],
    }),

    getCollectionFeatures: builder.query<IApiResponseWithPagination<Record<string, string>[]>, { id: ICollection["ID"]; filters?: IFilter[] } & IPagination>({
      query: ({ id, filters, ...params }) => ({
        url: `mapping/collections/${id}/features`,
        skipUnwrap: true,
        params: {
          ...params,
          ...(filters ? filtersToQueryStringObject(filters) : {}),
        },
      }),
      providesTags: (_r, _e, { id, ...params }) => [{ type: "CollectionFeatures", id: id + JSON.stringify(params) }],
    }),
    getCollectionColumnDefs: builder.query<GridOptions["columnDefs"], string | number>({
      query: (collectionId) => `mapping/collections/${collectionId}`,
      transformResponse: (r: { properties: { name: string; column: string; type: string }[] }) => {
        return r.properties.map(({ name, column }) => ({ headerName: name, field: column }));
      },
      providesTags: (_r, _e, id) => [{ type: "Collections", id }],
    }),
    getCollections: builder.query<ICollection[], { with?: ("themes" | "themes.rules")[] } | void>({
      query: (params) => ({
        url: `mapping/collections`,
        params: { with: params?.with ? params.with.join(",") : undefined },
      }),
      providesTags: (_r, _e) => [{ type: "Collections", id: "LIST" }],
    }),
    deleteCollection: builder.mutation<void, ICollection["ID"]>({
      query: (id) => ({ method: "delete", url: `mapping/collections/${id}` }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Collections", id: "LIST" },
        { type: "Collections", id },
      ],
    }),
    updateCollection: builder.mutation<void, Partial<ICollection> & Pick<ICollection, "ID">>({
      query: ({ ID, ...body }) => ({ method: "PATCH", url: `mapping/collections/${ID}`, body }),
      invalidatesTags: (_r, _e, { ID }) => [
        { type: "Collections", id: "LIST" },
        { type: "Collections", id: ID },
      ],
    }),

    getCollectionAggregates: builder.query<{ ID: number; ids: any[] }, { [key: string]: any }>({
      query: ({ ID, ids }) => ({ method: "POST", url: `mapping/collections/${ID}/aggregate`, body: { ids } }),
      providesTags: (_r, _e, body) => [{ type: "CollectionAggregates", id: `${body.ID}-${body.ids.join("-")}` }],
    }),

    /**
     * Get the complete list of workspaces available to a user with maps nested in them.
     */
    getWorkspacesWithMaps: builder.query<IWorkspace[], void>({
      query: () => `mapping/workspaces?createDefault=1&with=maps,maps.views,maps.reports`,
      providesTags: [{ type: "Workspaces", id: "LIST" }],
    }),

    /**
     * Perform a partial update on a workspace.
     */
    updateWorkspace: builder.mutation<void, IWorkspaceUpdate>({
      query: (body) => ({ method: "PATCH", url: `mapping/workspaces/${body.ID}`, body }),
      invalidatesTags: (resp, err, body) => [
        { type: "Workspaces", id: "LIST" },
        { type: "Workspaces", id: body.ID },
      ],
      // Apply this update to the cached data immediately so that the UI updates.
      async onQueryStarted({ ID, ...body }, { dispatch, queryFulfilled }) {
        const localResult = dispatch(
          baseApi.util.updateQueryData("getWorkspacesWithMaps", undefined, (workspaces) => workspaces.filter((w) => w.ID == ID).forEach((w) => Object.assign(w, body)))
        );
      },
    }),

    /**
     * Perform a partial update on a map.
     */
    updateMap: builder.mutation<void, IUpdateMap>({
      query: (body) => ({ method: "PATCH", url: `mapping/maps/${body.ID}`, body }),
      invalidatesTags: (resp, err, body) => [
        { type: "Workspaces", id: "LIST" },
        { type: "Workspaces", id: body.workspaceID },
        { type: "MapActions", id: `mapID-${body.ID}` },
        { type: "MapActionTypes", id: `mapID-${body.ID}` },
      ],
      // Apply this update to the map as cached by the getWorkspacesWithMaps query (immediately, so that the UI updates).
      async onQueryStarted({ ID, ...body }, { dispatch, queryFulfilled }) {
        const localResult = dispatch(
          baseApi.util.updateQueryData("getWorkspacesWithMaps", undefined, (workspaces) =>
            workspaces
              .filter((w) => w.ID == body.workspaceID)
              .flatMap((w) => w.maps)
              .flatMap((m) => [m, ...(m.views || [])]) // Added for backward-compatibility with API returning nested maps/views.
              .filter((m) => m.ID == ID)
              .forEach((m) => Object.assign(m, body))
          )
        );
      },
    }),

    /**
     * Create a new map.
     */
    createMap: builder.mutation<void, ICreateMap>({
      query: (body) => ({ method: "POST", url: "mapping/maps", body }),
      invalidatesTags: (resp, err, body) => [
        { type: "Workspaces", id: "LIST" },
        { type: "Workspaces", id: body.workspaceID },
      ],
    }),

    /**
     * Delete a map.
     */
    deleteMap: builder.mutation<void, IMap>({
      query: (body) => ({ method: "DELETE", url: `mapping/maps/${body.ID}` }),
      invalidatesTags: (resp, err, body) => [
        { type: "Workspaces", id: "LIST" },
        { type: "Workspaces", id: body.workspaceID },
      ],
    }),

    /**
     * Get the actions associated with a map.
     */
    getMapActions: builder.query<IMapAction[], number>({
      query: (mapID) => `mapping/actions?mapID=${mapID}`,
      providesTags: (_r, _e, mapID ) => [{ type: "MapActions", id: `mapID-${mapID}` }],
    }),

    /**
     * Get the possible action types for a map.
     */
    getMapActionTypes: builder.query<IMapActionType[], number[]>({
      query: (collectionIDs) => `mapping/action-types?collectionIDs=${collectionIDs.join(",")}`,
      providesTags: (_r, _e, mapID ) => [{ type: "MapActionTypes", id: `mapID-${mapID}` }],
    }),

    /**
     * Get any input requirements or validation results before running an action.
     */
    getMapActionPreflight: builder.query<ActionPreflightResponse, ActionPreflightRequest>({
      query: (req) => ({ method: "POST", url: `mapping/actions/${req.actionID}/preflight`, body: req }),
    }),

    /**
     * Invoke a map action.
     */
    invokeMapAction: builder.mutation<ActionInvokeResponse, ActionInvokeRequest>({
      query: (req) => ({ method: "POST", url: `mapping/actions/${req.actionID}/invoke`, body: req }),
    }),

    /**
     * Update a theme.
     */
    updateTheme: builder.mutation<void, ITheme>({
      query: (body) => ({ method: "PUT", url: `mapping/themes/${body.ID}`, body }),
      invalidatesTags: [], // @todo when we start fetching themes via RTK
    }),

    /**
     * Create a new theme.
     */
    createTheme: builder.mutation<ITheme, ITheme>({
      query: (body) => ({ method: "POST", url: "mapping/themes", body }),
      invalidatesTags: [], // @todo when we start fetching themes via RTK
    }),

    /**
     * Delete a map.
     */
    deleteTheme: builder.mutation<void, ITheme>({
      query: (body) => ({ method: "DELETE", url: `mapping/themes/${body.ID}` }),
      invalidatesTags: [], // @todo when we start fetching themes via RTK
    }),

    /**
     * Search for users by name
     */
    searchUsers: builder.query<IUser[], string>({
      query: (name) => ({ url: `users/search`, params: { name } }),
    }),

    validateCSS: builder.mutation<{ parsed?: string; errors?: Record<number, string[]> }, { css: string; label: string; fontSize: number }>({
      query: ({ css, ...variables }) => ({
        method: "POST",
        url: "mapping/themes/validate",
        body: {
          css,
          variables,
        },
      }),
    }),
  }),
});

export const {
  useUpdateUserPreferencesMutation,
  useGetFaqsQuery,
  useGetCollectionFeaturesQuery,
  useLazyGetCollectionFeaturesQuery,
  useGetCollectionColumnDefsQuery,
  useGetCollectionsQuery,
  useDeleteCollectionMutation,
  useUpdateCollectionMutation,
  useGetCollectionAggregatesQuery,
  useGetWorkspacesWithMapsQuery,
  useUpdateWorkspaceMutation,
  useUpdateMapMutation,
  useCreateMapMutation,
  useSearchUsersQuery,
  useUpdateThemeMutation,
  useCreateThemeMutation,
  useValidateCSSMutation,
  useGetMapActionsQuery,
  useGetMapActionTypesQuery,
  useGetMapActionPreflightQuery,
  useInvokeMapActionMutation,
} = baseApi;

const filtersToQueryStringObject = (filters: IFilter[]) => {
  const filterObject: { [key: string]: string } = {};
  filters.forEach(({ column, operator, value }, index) => {
    filterObject[`filters[${index}][operator]`] = operator;
    filterObject[`filters[${index}][column]`] = column;
    filterObject[`filters[${index}][value]`] = value.toString();
  });
  return filterObject;
};
